import { X, Clock, User, Package, DollarSign, Hash, ExternalLink } from 'lucide-react';
import { StatusBadge } from './StatusBadge';
import { cn, formatCurrency, formatDate, formatDuration } from '../lib/utils';
import type { Order } from '../types';

interface OrderDetailProps {
  order: Order | null;
  onClose: () => void;
}

export function OrderDetail({ order, onClose }: OrderDetailProps) {
  if (!order) return null;

  const timeToClose = order.checkoutSuccess && order.sessionStart
    ? new Date(order.checkoutSuccess).getTime() - new Date(order.sessionStart).getTime()
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 pb-4 px-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-2xl max-h-[calc(100vh-6rem)] overflow-y-auto bg-white dark:bg-oem-900 rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-oem-200 dark:border-oem-800 bg-white dark:bg-oem-900 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-oem-900 dark:text-white">Order Details</h2>
            <StatusBadge status={order.status} size="sm" />
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-oem-100 dark:hover:bg-oem-800 text-oem-500 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Order reference (PO #) */}
          <div className="flex items-center gap-2 text-sm text-oem-500">
            <Hash className="w-4 h-4" />
            <span className="font-medium text-oem-800">PO #{order.poNumber ?? order.id}</span>
            <span className={cn(
              'px-2 py-0.5 rounded text-xs font-medium ml-2',
              order.source === 'live'
                ? 'bg-oem-100 text-oem-800'
                : 'bg-status-intervention/10 text-status-intervention'
            )}>
              {order.source === 'live' ? 'live' : 'override'}
            </span>
            {order.abandoned && (
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-status-abandoned/10 text-status-abandoned">
                Abandoned
              </span>
            )}
          </div>

          {/* Customer Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-oem-500 uppercase tracking-wider">Customer</h3>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-oem-100 dark:bg-oem-800 rounded-lg">
                  <User className="w-5 h-5 text-oem-600 dark:text-oem-300" />
                </div>
                <div>
                  <p className="font-medium text-oem-900 dark:text-white">{order.customerName}</p>
                  <p className="text-sm text-oem-500">{order.customerEmail}</p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-medium text-oem-500 uppercase tracking-wider">Timeline</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-oem-400" />
                  <div>
                    <p className="text-sm text-oem-500">Session Started</p>
                    <p className="text-sm font-medium text-oem-900 dark:text-white">{formatDate(order.sessionStart)}</p>
                  </div>
                </div>
                {order.checkoutSuccess && (
                  <div className="flex items-center gap-3">
                    <CheckIcon />
                    <div>
                      <p className="text-sm text-oem-500">Checkout Success</p>
                      <p className="text-sm font-medium text-oem-900 dark:text-white">{formatDate(order.checkoutSuccess)}</p>
                    </div>
                  </div>
                )}
                {timeToClose && (
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-accent-500" />
                    <div>
                      <p className="text-sm text-oem-500">Time to Close</p>
                      <p className="text-sm font-medium text-accent-600">{formatDuration(timeToClose)}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Order Total */}
          <div className="flex items-center gap-4 p-4 bg-oem-100 rounded-xl">
            <div className="p-3 bg-white rounded-lg shadow-sm">
              <DollarSign className="w-6 h-6 text-accent-500" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-oem-muted">Order Total{order.poNumber ? ` · PO #${order.poNumber}` : ''}</p>
              <p className="text-2xl font-display text-oem-navy">{formatCurrency(order.total)}</p>
            </div>
            {(order.subtotal != null || order.shippingTotal != null || order.discountTotal != null || order.taxTotal != null) && (
              <div className="text-right text-sm space-y-0.5">
                {order.subtotal != null && (
                  <p className="text-oem-muted">Subtotal <span className="text-oem-800 font-medium ml-2">{formatCurrency(order.subtotal)}</span></p>
                )}
                {order.discountTotal != null && order.discountTotal > 0 && (
                  <p className="text-oem-muted">Discount <span className="text-status-automation font-medium ml-2">−{formatCurrency(order.discountTotal)}</span></p>
                )}
                {order.shippingTotal != null && (
                  <p className="text-oem-muted">Shipping <span className="text-oem-800 font-medium ml-2">{formatCurrency(order.shippingTotal)}</span></p>
                )}
                {order.taxTotal != null && (
                  <p className="text-oem-muted">Tax <span className="text-oem-800 font-medium ml-2">{formatCurrency(order.taxTotal)}</span></p>
                )}
              </div>
            )}
          </div>

          {/* Payment / Shipping / Seller */}
          {(order.paymentMethod || order.shippingMethodLabel || (order.sellerNames && order.sellerNames.length > 0)) && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {order.paymentMethod && (
                <div className="p-3 border border-oem-200 rounded-lg">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-oem-muted mb-1">Payment</p>
                  <p className="text-sm text-oem-800">{order.paymentMethod}</p>
                </div>
              )}
              {order.shippingMethodLabel && (
                <div className="p-3 border border-oem-200 rounded-lg">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-oem-muted mb-1">Shipping</p>
                  <p className="text-sm text-oem-800">{order.shippingMethodLabel}</p>
                </div>
              )}
              {order.sellerNames && order.sellerNames.length > 0 && (
                <div className="p-3 border border-oem-200 rounded-lg">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-oem-muted mb-1">Seller</p>
                  <p className="text-sm text-oem-800">{order.sellerNames.join(', ')}</p>
                </div>
              )}
            </div>
          )}

          {/* Line Items */}
          {order.items && order.items.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-oem-muted uppercase tracking-wider flex items-center gap-2">
                <Package className="w-4 h-4" />
                Line Items ({order.items.length})
              </h3>
              <div className="border border-oem-200 rounded-lg divide-y divide-oem-100 overflow-hidden">
                {order.items.map((item, i) => (
                  <div key={i} className="p-3 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-oem-900 leading-snug">{item.name}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <span className="px-2 py-0.5 bg-oem-100 rounded text-xs font-mono text-oem-700">{item.partNumber}</span>
                        {item.businessUnit && <span className="text-xs text-oem-muted">{item.businessUnit}</span>}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-semibold text-oem-900">{formatCurrency(item.unitPrice)} × {item.quantity}</p>
                      {item.listPrice != null && item.discountPercent != null && item.discountPercent > 0 && (
                        <p className="text-xs text-oem-muted">
                          <span className="line-through">{formatCurrency(item.listPrice)}</span>
                          <span className="text-status-automation ml-1">−{item.discountPercent}%</span>
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Part Numbers (fallback when line items are unavailable) */}
          {(!order.items || order.items.length === 0) && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-oem-500 uppercase tracking-wider flex items-center gap-2">
              <Package className="w-4 h-4" />
              Part Numbers ({order.partNumbers.length})
            </h3>
            {order.partNumbers.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {order.partNumbers.map((part, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-oem-100 dark:bg-oem-800 rounded-lg text-sm font-mono text-oem-700 dark:text-oem-300"
                  >
                    {part}
                    <a
                      href={`https://oemstream.com/search?q=${encodeURIComponent(part)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-oem-400 hover:text-accent-500"
                    >
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-oem-400">No part numbers recorded</p>
            )}
          </div>
          )}

          {/* Notes */}
          {order.notes && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-oem-500 uppercase tracking-wider">Notes</h3>
              <p className="text-sm text-oem-700 dark:text-oem-300 bg-oem-50 dark:bg-oem-800/50 p-3 rounded-lg">
                {order.notes}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg className="w-4 h-4 text-status-automation" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}
